import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Check, Sparkles, ArrowRight, Award, Zap } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";

const SINGLE_PRICE = 197;
const BUNDLE_PRICE = 720;
const COURSE_COUNT = 6;
const TOTAL_INDIVIDUAL = SINGLE_PRICE * COURSE_COUNT; // 1182
const SAVINGS = TOTAL_INDIVIDUAL - BUNDLE_PRICE; // 462
const DISCOUNT_PCT = Math.round((SAVINGS / TOTAL_INDIVIDUAL) * 100); // 39

const BUNDLE_HOTMART_URL = "https://pay.hotmart.com/J105612416Y";

const translations = {
  badge: { es: "La opción más popular", en: "Most popular option" },
  overline: { es: "Pack Completo", en: "Complete Bundle" },
  title: {
    es: "Los 6 cursos · Una sola inversión",
    en: "All 6 courses · One single investment",
  },
  subtitle: {
    es: "Domina toda la cadena de valor de la industria de reuniones con el plan de estudios completo. Pensado para quienes buscan transformación profesional integral.",
    en: "Master the entire meetings industry value chain with the complete curriculum. Designed for those seeking comprehensive professional transformation.",
  },
  includesTitle: { es: "Qué incluye el pack", en: "What's included" },
  includes: {
    es: [
      "Acceso a los 6 cursos especializados",
      "Certificado individual por cada curso completado",
      "Materiales descargables y plantillas profesionales",
      "Acceso flexible al contenido completo",
      "Soporte directo durante todo tu aprendizaje",
      "Comunidad exclusiva de profesionales LATAM",
    ],
    en: [
      "Access to all 6 specialized courses",
      "Individual certificate for each completed course",
      "Downloadable materials and professional templates",
      "Flexible access to the full content",
      "Direct support throughout your learning",
      "Exclusive LATAM professional community",
    ],
  },
  individualLabel: { es: "Comprándolos por separado", en: "If purchased individually" },
  bundleLabel: { es: "Pack Completo", en: "Complete Bundle" },
  saveLabel: { es: "Ahorras", en: "You save" },
  perCourse: { es: "Equivale a", en: "Works out to" },
  perCourseValue: {
    es: `$${Math.round(BUNDLE_PRICE / COURSE_COUNT)} USD por curso`,
    en: `$${Math.round(BUNDLE_PRICE / COURSE_COUNT)} USD per course`,
  },
  cta: { es: "Quiero el pack completo", en: "Get the complete bundle" },
  guarantee: {
    es: "Garantía de retracto de 7 días · Pago seguro a través de Hotmart",
    en: "7-day refund guarantee · Secure payment via Hotmart",
  },
};

const BundleSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useLanguage();

  const includes = t(translations.includes);

  return (
    <section
      id="pack"
      data-testid="bundle-section"
      ref={ref}
      className="relative py-24 md:py-32 bg-[#0B1F3B] overflow-hidden"
    >
      {/* Decorative orbs */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#F59E0B]/15 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#2ED3C6]/10 rounded-full blur-[120px] translate-x-1/3 translate-y-1/3 pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <span className="inline-flex items-center gap-2 text-[#F59E0B] text-xs md:text-sm uppercase tracking-[0.25em] font-bold font-body mb-4">
            <Sparkles className="w-4 h-4" />
            {t(translations.overline)}
            <Sparkles className="w-4 h-4" />
          </span>
          <h2
            data-testid="bundle-title"
            className="font-display text-3xl md:text-4xl lg:text-5xl text-white font-medium tracking-tight mb-5"
          >
            {t(translations.title)}
          </h2>
          <p className="font-body text-white/70 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            {t(translations.subtitle)}
          </p>
        </motion.div>

        {/* Main bundle card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="relative"
        >
          {/* Floating badge */}
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#F59E0B] text-[#0B1F3B] rounded-full text-xs font-display font-bold uppercase tracking-wider shadow-lg shadow-[#F59E0B]/30">
              <Award className="w-3.5 h-3.5" />
              {t(translations.badge)}
            </div>
          </div>

          <div className="bg-gradient-to-br from-white/[0.06] to-white/[0.02] backdrop-blur-sm border border-[#F59E0B]/30 rounded-3xl p-8 md:p-12 lg:p-14">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
              {/* Left: Includes */}
              <div className="lg:col-span-7">
                <h3 className="font-display text-xl md:text-2xl text-white font-semibold mb-6 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-[#2ED3C6]" />
                  {t(translations.includesTitle)}
                </h3>
                <ul data-testid="bundle-includes" className="space-y-3.5">
                  {includes.map((item, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      animate={isInView ? { opacity: 1, x: 0 } : {}}
                      transition={{ duration: 0.4, delay: 0.3 + i * 0.07 }}
                      className="flex items-start gap-3 font-body text-white/85 text-base"
                    >
                      <span className="mt-1 w-5 h-5 rounded-full bg-[#2ED3C6]/20 flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3 text-[#2ED3C6]" strokeWidth={3} />
                      </span>
                      {item}
                    </motion.li>
                  ))}
                </ul>
              </div>

              {/* Right: Price block */}
              <div className="lg:col-span-5">
                <div className="bg-[#0B1F3B]/60 border border-white/10 rounded-2xl p-6 md:p-8">
                  {/* Crossed-out individual price */}
                  <div className="mb-1">
                    <span className="font-body text-xs text-white/40 uppercase tracking-wider">
                      {t(translations.individualLabel)}
                    </span>
                  </div>
                  <div className="mb-5">
                    <span className="font-display text-2xl text-white/40 line-through decoration-[#F59E0B]/60 decoration-2">
                      ${TOTAL_INDIVIDUAL} USD
                    </span>
                  </div>

                  {/* Bundle price */}
                  <div className="mb-2">
                    <span className="font-body text-xs text-[#2ED3C6] uppercase tracking-wider font-semibold">
                      {t(translations.bundleLabel)}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2 mb-5">
                    <span
                      data-testid="bundle-price"
                      className="font-display text-5xl md:text-6xl text-white font-bold tracking-tight"
                    >
                      ${BUNDLE_PRICE}
                    </span>
                    <span className="font-display text-xl text-[#2ED3C6] font-medium">USD</span>
                  </div>

                  {/* Savings highlight */}
                  <div className="flex items-center justify-between p-4 bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-xl mb-5">
                    <div>
                      <div className="font-body text-xs text-[#F59E0B] uppercase tracking-wider font-bold mb-1">
                        {t(translations.saveLabel)}
                      </div>
                      <div className="font-display text-xl text-white font-semibold">
                        ${SAVINGS} USD
                      </div>
                    </div>
                    <div className="font-display text-3xl md:text-4xl text-[#F59E0B] font-bold">
                      -{DISCOUNT_PCT}%
                    </div>
                  </div>

                  {/* Per-course equivalence */}
                  <div className="text-center mb-5 font-body text-sm text-white/60">
                    {t(translations.perCourse)}{" "}
                    <span className="text-white font-semibold">{t(translations.perCourseValue)}</span>
                  </div>

                  {/* CTA */}
                  <a
                    href={BUNDLE_HOTMART_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="bundle-cta"
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#F59E0B] hover:bg-[#D97706] text-[#0B1F3B] font-display font-bold py-4 px-6 rounded-full text-base transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-[#F59E0B]/30 group"
                  >
                    {t(translations.cta)}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </a>

                  {/* Trust note */}
                  <p className="mt-4 text-center font-body text-xs text-white/40 leading-relaxed">
                    {t(translations.guarantee)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default BundleSection;
